/** @jsxImportSource @emotion/react */
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import Button from 'react-bootstrap/Button'
import { toast } from 'react-toastify'

import { confirm } from 'components/molecules/Confirmation'
import { useClearOfficeNotesScreeningMutation } from 'store/services/labOrder'
import { Order } from 'store/types'

type ScreeningFinding = {
  condition: string
  quote: string
  certain: boolean
}

type Screening = {
  fileKey: string
  status: 'PENDING' | 'SCREENED' | 'FAILED'
  findings: ScreeningFinding[]
  error: string | null
  clearedAt: string | null
}

// Flags intake has to look at: a condition the AI found, or notes it could not read, on office
// notes still on the order, not yet cleared. Mirrors activeOfficeNotesFlags on the server.
export const activeOfficeNotesFlags = (order?: Order): Screening[] => {
  if (!order || order.testType !== 'HST' || order.cancelled) {
    return []
  }

  return Object.values<Screening>((order.officeNotesScreenings || {}) as Record<string, Screening>).filter(
    (screening) =>
      !!order.files?.[screening.fileKey] &&
      !screening.clearedAt &&
      (screening.status === 'FAILED' || (screening.status === 'SCREENED' && screening.findings.length > 0)),
  )
}

// The conditions found, to check on the Patient Disqualified for HST cancel.
export const officeNotesFlaggedConditions = (order?: Order): string[] =>
  Array.from(
    new Set(activeOfficeNotesFlags(order).flatMap(({ findings }) => findings.map(({ condition }) => condition))),
  )

// Shown by the scheduling steps whose buttons are off while the flag waits for intake.
export const OfficeNotesHoldNote = () => (
  <div
    className="d-flex align-items-center mt-2"
    css={{ gap: 6, fontSize: 13, color: '#92400E' }}
    data-testid="office-notes-hold-note">
    <InfoOutlinedIcon css={{ fontSize: 16 }} />
    On hold for the office notes review above: clear the flag or cancel the order to go on.
  </div>
)

const styles = {
  panel: {
    background: '#FFFBEB',
    border: '1px solid #FCD34D',
    borderLeft: '4px solid #F59E0B',
    borderRadius: 6,
    padding: '14px 16px',
    marginBottom: 12,
  },
  header: { display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap' as const, gap: 12 },
  iconBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: '#FEF3C7',
    color: '#B45309',
  },
  title: { fontSize: 15, fontWeight: 600, color: '#78350F', lineHeight: 1.3 },
  subtitle: { fontSize: 13, color: '#92400E', marginTop: 2 },
  actions: { display: 'flex', gap: 8, flexShrink: 0 },
  clearButton: { background: 'white', borderColor: '#D1D5DB' },
  documents: { display: 'flex', flexDirection: 'column' as const, gap: 8, marginTop: 12, paddingLeft: 48 },
  document: { background: 'white', border: '1px solid #FDE68A', borderRadius: 4, padding: '10px 12px' },
  fileName: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 6,
  },
  finding: { '& + &': { marginTop: 8 } },
  condition: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: '#1F2937' },
  unsure: {
    fontSize: 11,
    fontWeight: 500,
    color: '#4B5563',
    background: '#F3F4F6',
    border: '1px solid #E5E7EB',
    borderRadius: 10,
    padding: '0 8px',
  },
  quote: { fontSize: 13, color: '#374151', borderLeft: '3px solid #FCD34D', paddingLeft: 8, marginTop: 4 },
  unreadable: { display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 13, color: '#374151' },
}

interface IProps {
  order: Order
  onCancelAsDisqualified: () => void
}

// The AI read the office notes for conditions that rule out home testing and found one
// (TES-3229). Intake confirms and cancels, or clears the flag and the order goes on.
export const OfficeNotesScreeningPanel = ({ order, onCancelAsDisqualified }: IProps) => {
  const [clearFlag, { isLoading: isClearing }] = useClearOfficeNotesScreeningMutation()
  const flags = activeOfficeNotesFlags(order)

  if (!flags.length) {
    return null
  }

  const handleClear = () =>
    confirm('', {
      title: 'Clear the flag?',
      description:
        'The patient is fine to test at home: the order goes on, and the paperwork link goes out if it was held.',
      yesAction: 'Clear flag',
      noAction: 'Back',
    })
      .then(() => clearFlag({ orderId: Number(order.id) }).unwrap())
      .catch((error: any) => {
        if (error?.data?.message) {
          toast.error(error.data.message)
        }
      })

  const conditionCount = flags.reduce((count, { findings }) => count + findings.length, 0)
  const title = conditionCount
    ? `Possible HST disqualifying condition${conditionCount > 1 ? 's' : ''} in the office notes`
    : 'The AI could not check the office notes'

  return (
    <div data-testid="office-notes-screening-flag" css={styles.panel}>
      <div css={styles.header}>
        <span css={styles.iconBadge}>
          <WarningAmberRoundedIcon css={{ fontSize: 22 }} />
        </span>
        <div css={{ flex: 1, minWidth: 220 }}>
          <div css={styles.title}>{title}</div>
          <div css={styles.subtitle}>
            Paperwork, the payment link and calls to the patient are on hold until the flag is cleared or the
            order is cancelled.
          </div>
        </div>
        <div css={styles.actions}>
          <Button variant="light" size="sm" css={styles.clearButton} disabled={isClearing} onClick={handleClear}>
            Clear flag
          </Button>
          <Button variant="danger" size="sm" onClick={onCancelAsDisqualified}>
            Cancel as disqualified
          </Button>
        </div>
      </div>
      <div css={styles.documents}>
        {flags.map((flag) => (
          <div key={flag.fileKey} css={styles.document}>
            <div css={styles.fileName}>
              <DescriptionOutlinedIcon css={{ fontSize: 15, flexShrink: 0 }} />
              <span css={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {order.files?.[flag.fileKey]?.filename || 'Office notes'}
              </span>
            </div>
            {flag.status === 'FAILED' ? (
              <div css={styles.unreadable}>
                <ErrorOutlineRoundedIcon css={{ fontSize: 18, color: '#B45309', flexShrink: 0 }} />
                <span>
                  The AI could not read these notes{flag.error ? ` (${flag.error})` : ''}. Review them for
                  disqualifying conditions.
                </span>
              </div>
            ) : (
              flag.findings.map(({ condition, quote, certain }) => (
                <div key={condition} css={styles.finding}>
                  <div css={styles.condition}>
                    {condition}
                    {!certain && <span css={styles.unsure}>Unsure</span>}
                  </div>
                  {quote && <div css={styles.quote}>“{quote}”</div>}
                </div>
              ))
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
